import React, { useState, useContext, useEffect } from "react";
import { UserContext } from "../../utils/userContext";
import axios from "../../utils/axiosInstance";
import SortableList, { DragHandle } from "../ui/SortableList";
import SubtaskRow from "./SubtaskRow";
import TaskSubtaskDetailModal from "./TaskSubtaskDetailModal";
import HoursProgressBar from "../ui/HoursProgressBar";

/**
 * Card component displaying a single task with its subtasks
 * Shows progress, allows expanding/collapsing, displays subtask rows
 * 
 * @param {boolean} readOnly - If true, hides all edit actions
 * @param {boolean} compact - If true, shows minimal UI without progress bars
 */
const TaskCard = ({ task, taskIdx, bundleId, onUpdate, dragHandleProps, readOnly = false, compact = false }) => {
  const { user } = useContext(UserContext);
  const canEdit = user?.isAdmin && !readOnly;
  const [isExpanded, setIsExpanded] = useState(!compact); // collapsed by default in compact mode
  const [showDetailModal, setShowDetailModal] = useState(false);
  // Local state for subtasks that syncs with prop
  const [subtasks, setSubtasks] = useState(task.subtasks || []);

  // Sync local subtasks state when task prop changes
  useEffect(() => {
    setSubtasks(task.subtasks || []);
  }, [task.subtasks]);

  const handleReorderSubtasks = async (reorderedItems) => {
    if (!canEdit || !bundleId) return;
    
    // Update local state optimistically with the new order
    const newSubtasks = reorderedItems.map(item => item.subtask);
    setSubtasks(newSubtasks);
    
    try {
      const subtaskIds = reorderedItems.map(item => item.id);
      await axios.put(`/task-bundles/${bundleId}/tasks/${taskIdx}/reorder-subtasks`, { subtaskIds });
    } catch (error) {
      console.error("Error reordering subtasks:", error);
      alert("Failed to reorder subtasks");
      // Revert by refetching on error
      onUpdate();
    }
  };

  // Calculate task progress using local subtasks state
  const calculateProgress = () => {
    if (!subtasks || subtasks.length === 0) return 0;
    
    const statusValues = { todo: 0, in_progress: 0.5, done: 1, blocked: 0 };
    let totalWeight = 0;
    let weightedProgress = 0;
    
    subtasks.forEach(subtask => {
      const weight = subtask.weight || 1;
      const progress = statusValues[subtask.status] || 0;
      totalWeight += weight;
      weightedProgress += weight * progress;
    });
    
    return totalWeight > 0 ? Math.round((weightedProgress / totalWeight) * 100) : 0;
  };

  const progress = calculateProgress();
  const completedCount = subtasks?.filter(s => s.status === 'done').length || 0;
  const totalCount = subtasks?.length || 0;

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo':
        return 'bg-gray-100 text-gray-700';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700';
      case 'done':
        return 'bg-green-100 text-green-700';
      case 'blocked':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'todo':
        return 'To Do';
      case 'in_progress':
        return 'In Progress';
      case 'done':
        return 'Done';
      case 'blocked':
        return 'Blocked';
      default:
        return status;
    }
  };

  const getBillingModelLabel = (billingModel) => {
    switch (billingModel) {
      case 'hourly':
        return 'Hourly/Ongoing';
      case 'fixed_price':
        return 'Fixed Price';
      default:
        return null;
    }
  };

  // Compact mode - minimal tree-like view
  if (compact) {
    return (
      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
        {/* Compact Task Header */}
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {/* Drag Handle */}
            {dragHandleProps && (
              <DragHandle 
                listeners={dragHandleProps}
                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
              />
            )}
            
            {/* Expand/Collapse */}
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600 p-0.5"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            {/* Task Title */}
            <span 
              className="font-medium text-sm text-gray-900 flex-1 cursor-pointer hover:text-blue-600"
              onClick={() => setShowDetailModal(true)}
            >
              {task.title}
            </span>
            
            {/* Billing Model Badge */}
            {task.billingModel && (
              <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                {getBillingModelLabel(task.billingModel)}
              </span>
            )}
            
            {/* Subtask Count */}
            <span className="text-xs text-gray-500">
              {totalCount} {totalCount === 1 ? 'subtask' : 'subtasks'}
            </span>
            
            {/* Edit Button */}
            {canEdit && (
              <button
                type="button"
                onClick={() => setShowDetailModal(true)}
                className="text-gray-400 hover:text-blue-600 p-1"
                title="Edit task"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Compact Subtasks List */}
        {isExpanded && subtasks && subtasks.length > 0 && (
          <div className="px-3 py-2">
            {canEdit ? (
              <SortableList
                items={subtasks.map((subtask, idx) => ({
                  id: subtask._id,
                  subtask,
                  subtaskIdx: idx
                }))}
                onReorder={handleReorderSubtasks}
                renderItem={(item, dragHandleProps) => (
                  <SubtaskRow
                    subtask={item.subtask}
                    taskIdx={taskIdx}
                    subtaskIdx={item.subtaskIdx}
                    bundleId={bundleId}
                    onUpdate={onUpdate}
                    dragHandleProps={dragHandleProps}
                    readOnly={readOnly}
                    compact={compact}
                  />
                )}
              />
            ) : (
              <div className="space-y-1">
                {subtasks.map((subtask, subtaskIdx) => (
                  <SubtaskRow
                    key={subtaskIdx}
                    subtask={subtask}
                    taskIdx={taskIdx}
                    subtaskIdx={subtaskIdx}
                    bundleId={bundleId}
                    onUpdate={onUpdate}
                    readOnly={readOnly}
                    compact={compact}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Task Detail Modal */}
        <TaskSubtaskDetailModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          type="task"
          data={task}
          bundleId={bundleId}
          taskIdx={taskIdx}
          onUpdate={onUpdate}
          readOnly={readOnly}
        />
      </div>
    );
  }

  // Full mode - original UI with progress bars
  return (
    <div className="bg-gray-100">
      {/* Task Header */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {/* Drag Handle - only shown for admins */}
              {dragHandleProps && (
                <div className="flex-shrink-0">
                  <DragHandle 
                    listeners={dragHandleProps}
                    className="cursor-grab active:cursor-grabbing p-1 text-gray-900 hover:text-gray-700 transition-colors"
                  />
                </div>
              )}
              <h4 
                className="font-semibold text-gray-900 hover:underline cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetailModal(true);
                }}
              >
                {task.title}
              </h4>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status || 'todo')}`}>
                {getStatusLabel(task.status || 'todo')}
              </span>
              {/* Billing Model Badge */}
              {task.billingModel && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                  {getBillingModelLabel(task.billingModel)}
                </span>
              )}
            </div>

            {/* Hours: show reported vs estimated, or reported only when no estimate */}
            {(task.estimatedHours != null || (task.trackedHours != null && task.trackedHours > 0)) && (
              <div className="mb-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500">Hours</span>
                </div>
                <HoursProgressBar 
                  estimatedHours={task.estimatedHours} 
                  reportedHours={task.trackedHours ?? 0} 
                />
              </div>
            )}

            {/* Progress Bar */}
            <div className="mb-2">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-500">Progress</span>
                <span className="font-medium">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Subtask Count */}
            <div className="flex items-center gap-3 mt-4">
              <span className="text-sm text-gray-900 font-medium">
                {totalCount} {totalCount === 1 ? 'Subtask' : 'Subtasks'}
              </span>
              <div className="flex-1 border-t border-gray-900"></div>
              <button
                type="button"
                className="text-gray-900 hover:text-gray-600"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <svg
                  className={`w-5 h-5 transition-transform ${
                    isExpanded ? 'transform rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Subtasks List - using local state */}
      {isExpanded && subtasks && subtasks.length > 0 && (
        <div className="px-4 pb-4">
          {canEdit ? (
            <SortableList
              items={subtasks.map((subtask, idx) => ({
                id: subtask._id,
                subtask,
                subtaskIdx: idx
              }))}
              onReorder={handleReorderSubtasks}
              renderItem={(item, dragHandleProps) => (
                <div className="border-b border-gray-200">
                  <SubtaskRow
                    subtask={item.subtask}
                    taskIdx={taskIdx}
                    subtaskIdx={item.subtaskIdx}
                    bundleId={bundleId}
                    onUpdate={onUpdate}
                    dragHandleProps={dragHandleProps}
                    readOnly={readOnly}
                  />
                </div>
              )}
            />
          ) : (
            <div className="divide-y divide-gray-200">
              {subtasks.map((subtask, subtaskIdx) => (
                <SubtaskRow
                  key={subtaskIdx}
                  subtask={subtask}
                  taskIdx={taskIdx}
                  subtaskIdx={subtaskIdx}
                  bundleId={bundleId}
                  onUpdate={onUpdate}
                  readOnly={readOnly}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Task Detail Modal */}
      <TaskSubtaskDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        type="task"
        data={task}
        bundleId={bundleId}
        taskIdx={taskIdx}
        onUpdate={onUpdate}
        readOnly={readOnly}
      />
    </div>
  );
};

export default TaskCard;
